/**
 * Signals V3 PR 2, U4 — the day-level "Step since" value (plan
 * 2026-10-20-005, Key Technical Decision 2).
 *
 * `step_started_at` is a `YYYY-MM-DD` calendar date on the wire (a DATE
 * column, `gong_engage_flow.last_step_completed_date` falling back to
 * `created_date`), so hour precision is not available: the date is read as
 * LOCAL midnight and compared against `today`'s local calendar day. Never
 * `new Date(str)` — that parses a bare date as UTC midnight and shifts it a
 * day west of Greenwich.
 */

export type StepSinceTone = 'success' | 'critical' | 'muted';

export interface StepSince {
  /** Whole calendar days since the step started; `null` when unknown. */
  days: number | null;
  label: string;
  tone: StepSinceTone;
}

const MS_PER_DAY = 86_400_000;
const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const UNKNOWN: StepSince = { days: null, label: '—', tone: 'muted' };

/**
 * `Date.UTC` on the local y/m/d parts gives a day number free of DST: both
 * sides are UTC midnights, so the difference is always a whole multiple of
 * 24h even across a 23- or 25-hour local day.
 */
function utcDayNumber(y: number, m: number, d: number): number {
  return Date.UTC(y, m, d) / MS_PER_DAY;
}

export function computeStepSince(stepStartedAt: string | null, today: Date): StepSince {
  if (!stepStartedAt) return UNKNOWN;
  const match = DATE_RE.exec(stepStartedAt);
  if (!match) return UNKNOWN;
  const y = Number(match[1]);
  const m = Number(match[2]) - 1;
  const d = Number(match[3]);
  // Reject impossible dates (`2026-02-30` would roll over to March 2).
  const local = new Date(y, m, d);
  if (local.getFullYear() !== y || local.getMonth() !== m || local.getDate() !== d) return UNKNOWN;
  if (Number.isNaN(today.getTime())) return UNKNOWN;

  const days = utcDayNumber(today.getFullYear(), today.getMonth(), today.getDate()) - utcDayNumber(y, m, d);
  if (days < 0) return UNKNOWN; // future date — clock skew, not "Today"
  if (days === 0) return { days: 0, label: 'Today', tone: 'success' };
  return { days, label: `${days}d`, tone: 'critical' };
}
